import { useEffect, useMemo, useRef } from "react";
import { useQueries } from "@tanstack/react-query";
import type { Map as MapboxMap } from "mapbox-gl";

import { StorySceneAnimation } from "../../../types/storyMap";
import {
  storyAnimationQueryOptions,
  StoryAnimationData,
} from "../../../api/storyMaps";
import { entryProjectId } from "../layerEntries";
import {
  boundsToCoordinates,
  renderConfidenceFrame,
  renderMetricFrame,
} from "../../layer_components/fire/fireCanvasEngine";
import {
  METRIC_TO_VIEW,
  renderTintedConfidence,
  renderTintedFrame,
} from "../useSceneAnimations";
import {
  StoryCanvasLayer,
  URBAN_STATE_FILL_PAINT,
  URBAN_STATE_LINE_PAINT,
} from "../storyLayers";

export interface FinalFrameLayersState {
  /** One canvas layer per ready entry, in scene.animations order. */
  layers: StoryCanvasLayer[];
  /** True once every entry's data fetch has settled (loaded or failed). */
  settled: boolean;
  /**
   * True while at least one entry's data is still downloading. Confidence
   * stacks frozen before frame packs run to hundreds of MB, so the capture
   * pipeline uses this to keep waiting instead of giving up on a fixed timer.
   */
  fetching: boolean;
}

/**
 * Static variant of useSceneAnimations for the print capture: fetches each
 * animation entry's arrays exactly like the live hook, but draws every entry
 * ONCE at its FINAL frame (arrival complete; confidence at its last frame) —
 * no clock, no playback controls. Reuses the live hook's render functions so
 * print output matches the on-screen animation end state.
 */
export function useFinalFrameLayers({
  animations,
  defaultProjectId = null,
  isPublic,
  enabled,
  layerIdPrefix = "print-anim",
  assetsTilesUrl = null,
  assetsTilesUrlByProject,
  getMap,
}: {
  animations: StorySceneAnimation[] | null | undefined;
  /** Project stories (authed): the project entries without a projectId cite. */
  defaultProjectId?: number | null;
  isPublic: boolean;
  enabled: boolean;
  layerIdPrefix?: string;
  /** Project asset tiles — enables the burnt/burning buildings still. */
  assetsTilesUrl?: string | null;
  /** Folder / organisation-wide stories: asset tiles per cited project. */
  assetsTilesUrlByProject?: Record<string, string | null>;
  /** Capture map handle, for applying the buildings' feature-states. */
  getMap?: () => MapboxMap | null;
}): FinalFrameLayersState {
  const entries = useMemo(() => animations ?? [], [animations]);
  const getMapRef = useRef(getMap);
  getMapRef.current = getMap;

  const queries = useQueries({
    queries: entries.map((entry) => {
      const frozenUrl = entry.frozen?.framesUrl ?? null;
      const publicUnavailable =
        isPublic && (!!entry.frozen?.stale || !frozenUrl);
      const entryPid = entryProjectId(entry, defaultProjectId);
      return storyAnimationQueryOptions({
        projectId: entryPid !== null ? String(entryPid) : undefined,
        simulationId: entry.simulationId,
        view: entry.view,
        partition: entry.partition,
        resolvedMember: entry.resolvedMember ?? null,
        percentile: entry.percentile ?? null,
        publicUrl: isPublic ? frozenUrl : null,
        enabled: enabled && !publicUnavailable,
      });
    }),
  });

  const canvasesRef = useRef<Map<string, HTMLCanvasElement>>(new Map());

  // Entries whose query can never produce data (unconfigured, frozen missing,
  // or errored) count as settled — the capture proceeds without them.
  const settled = entries.every((entry, i) => {
    const q = queries[i];
    if (q.data || q.isError) return true;
    if (isPublic) return !!entry.frozen?.stale || !entry.frozen?.framesUrl;
    if (!entry.simulationId) return true;
    if (entry.view === "single" && entry.partition == null) return true;
    if (
      entry.view === "percentile" &&
      entry.resolvedMember == null &&
      entry.percentile == null
    )
      return true;
    return false;
  });

  const fetching = queries.some((q) => q.fetchStatus === "fetching");

  const dataKey = queries.map((q) => (q.data ? "1" : "0")).join("");

  // End-of-run building states: every ignited building is burning or (if its
  // burnout falls within the run) burnt at the final frame. Urban times are
  // relative to each entry's first frame, so compare against that entry's
  // elapsed run length; merging mirrors the live hook (earliest ignition wins,
  // together with that entry's burnout).
  const finalUrbanStates = useMemo(() => {
    const ignitionAt = new Map<number, number>();
    const states = new Map<number, "burning" | "burnt">();
    entries.forEach((_entry, i) => {
      const data: StoryAnimationData | undefined = queries[i]?.data;
      if (!data || data.kind !== "frames") return;
      const urban = data.frames.urban;
      const ignS = urban?.urban_asset_ignition_s;
      if (!ignS) return;
      const times = data.frames.frameTimesSeconds;
      const elapsed = (times[times.length - 1] ?? 0) - (times[0] ?? 0);
      const burnS = urban?.urban_asset_burnout_s;
      for (const [assetId, s] of Object.entries(ignS)) {
        const id = Number(assetId);
        const prev = ignitionAt.get(id);
        if (prev !== undefined && prev <= s) continue;
        ignitionAt.set(id, s);
        const bo = burnS?.[assetId];
        states.set(id, bo !== undefined && bo <= elapsed ? "burnt" : "burning");
      }
    });
    return states;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey, entries]);

  // One buildings source per cited project (see useSceneAnimations).
  const urbanSources = useMemo(() => {
    const out: { id: string; url: string }[] = [];
    if (!assetsTilesUrlByProject) {
      if (assetsTilesUrl) out.push({ id: `${layerIdPrefix}-urban-buildings`, url: assetsTilesUrl });
      return out;
    }
    const seen = new Set<string>();
    entries.forEach((entry) => {
      const pid = entryProjectId(entry, defaultProjectId);
      const key = pid !== null ? String(pid) : "";
      const url = key ? assetsTilesUrlByProject[key] ?? null : assetsTilesUrl;
      if (!url || seen.has(key)) return;
      seen.add(key);
      out.push({ id: key ? `${layerIdPrefix}-urban-buildings-${key}` : `${layerIdPrefix}-urban-buildings`, url });
    });
    return out;
  }, [assetsTilesUrlByProject, assetsTilesUrl, entries, defaultProjectId, layerIdPrefix]);
  const urbanSourcesKey = urbanSources.map((s) => `${s.id}=${s.url}`).join("|");
  const showUrban = enabled && urbanSources.length > 0 && finalUrbanStates.size > 0;

  // Apply the states once the capture map has attached the buildings source.
  // The capture pipeline holds off until every declared source is on the map
  // and then waits for "idle" (tiles loading), so a short poll comfortably
  // lands the states before the PNG is read back.
  useEffect(() => {
    if (!showUrban) return;
    const timer = setInterval(() => {
      const map = getMapRef.current?.() ?? null;
      if (!map || !urbanSources.every((s) => !!map.getSource(s.id))) return;
      clearInterval(timer);
      for (const [id, state] of finalUrbanStates) {
        urbanSources.forEach((s) =>
          map.setFeatureState({ source: s.id, sourceLayer: "assets", id }, { urbanState: state })
        );
      }
    }, 100);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUrban, urbanSourcesKey, finalUrbanStates]);

  const layers = useMemo<StoryCanvasLayer[]>(() => {
    if (!enabled) return [];
    const out: StoryCanvasLayer[] = [];
    entries.forEach((entry, i) => {
      const data: StoryAnimationData | undefined = queries[i]?.data;
      if (!data) return;
      let canvas = canvasesRef.current.get(entry.id);
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvasesRef.current.set(entry.id, canvas);
      }
      const [shapeH, shapeW] =
        data.kind === "frames" ? data.frames.shape : data.confidence.shape;
      canvas.width = shapeW;
      canvas.height = shapeH;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const img = ctx.createImageData(shapeW, shapeH);
      if (data.kind === "confidence") {
        const finalIdx = data.confidence.frameCount - 1;
        if (entry.color) {
          renderTintedConfidence(img, data.confidence, finalIdx, entry.color);
        } else {
          renderConfidenceFrame(img, data.confidence, finalIdx);
        }
      } else {
        const finalIdx = data.frames.frameTimesSeconds.length - 1;
        if (entry.color) {
          renderTintedFrame(img, data.frames, finalIdx, entry.color);
        } else {
          renderMetricFrame(
            img,
            METRIC_TO_VIEW[entry.metric] ?? "rate_of_spread",
            data.frames,
            finalIdx
          );
        }
      }
      ctx.putImageData(img, 0, 0);
      const bounds =
        data.kind === "frames" ? data.frames.bounds : data.confidence.bounds;
      out.push({
        id: `${layerIdPrefix}-${entry.id}`,
        kind: "canvas-anim",
        canvas,
        coordinates: boundsToCoordinates(bounds),
        opacity: Math.min(1, Math.max(0.2, entry.opacity ?? 0.85)),
        // Drawn once at the final frame — a static source lets the capture
        // map reach "idle" (an animated one repaints forever).
        animate: false,
        visible: true,
      });
    });
    // Burnt/burning building footprints above the rasters, matching the live
    // animation's end state (states applied by the effect above).
    if (showUrban) {
      urbanSources.forEach((source) => {
        out.push({
          id: source.id,
          kind: "vector",
          url: source.url,
          sourceLayer: "assets",
          paint: URBAN_STATE_FILL_PAINT,
          linePaint: URBAN_STATE_LINE_PAINT,
          minzoom: 0,
          // Must match the backend asset-tile max zoom (PMTILES_MAX_ZOOM = 12).
          maxzoom: 12,
          tileSize: 512,
          promoteId: "__id",
          visible: true,
        });
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entries, dataKey, layerIdPrefix, showUrban, urbanSourcesKey]);

  return { layers, settled, fetching };
}
